/**
 * Input validation utilities for security
 */

// Helper to return translatable validation results
const validationError = (errorKey, fallback, errorParams) => ({
  valid: false,
  errorKey,
  errorParams,
  error: fallback || errorKey,
});

// Username validation - prevent XSS and ensure safe characters
export const validateUsername = (username) => {
  if (!username || typeof username !== 'string') {
    return validationError('usernameRequired', 'Username is required');
  }
  
  const trimmed = username.trim();
  
  // Length check
  if (trimmed.length < 3 || trimmed.length > 30) {
    return validationError('usernameLength', 'Username must be 3-30 characters');
  }
  
  // Character whitelist - only alphanumeric, underscore, hyphen
  const validPattern = /^[a-zA-Z0-9_-]+$/;
  if (!validPattern.test(trimmed)) {
    return validationError('usernameInvalidChars', 'Username can only contain letters, numbers, underscore, and hyphen');
  }
  
  // Email check
  if (/@/.test(trimmed)) {
    return validationError('usernameNoEmail', 'Username must not be an email address');
  }
  
  // Reserved words check
  const reservedWords = ['admin', 'root', 'system', 'null', 'undefined'];
  if (reservedWords.includes(trimmed.toLowerCase())) {
    return validationError('usernameReserved', 'This username is reserved');
  }
  
  return { valid: true, sanitized: trimmed };
};

// Team name validation - prevent XSS and ensure safe characters
export const validateTeamName = (teamName) => {
  if (!teamName || typeof teamName !== 'string') {
    return validationError('teamNameRequired', 'Team name is required');
  }
  
  const trimmed = teamName.trim();
  
  // Length check
  if (trimmed.length < 2 || trimmed.length > 50) {
    return validationError('teamNameLength', 'Team name must be 2-50 characters');
  }
  
  // Character whitelist - alphanumeric, spaces, and safe punctuation
  const validPattern = /^[a-zA-Z0-9\s\-_.()]+$/;
  if (!validPattern.test(trimmed)) {
    return validationError('teamNameInvalidChars', 'Team name contains invalid characters');
  }
  
  // Prevent script tags and potential XSS
  const dangerousPatterns = [
    /<script/i,
    /<\/script/i,
    /javascript:/i,
    /on\w+=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i
  ];
  
  for (const pattern of dangerousPatterns) {
    if (pattern.test(trimmed)) {
      return validationError('teamNameForbiddenContent', 'Team name contains forbidden content');
    }
  }
  
  return { valid: true, sanitized: trimmed };
};

// Email validation
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return validationError('emailRequired', 'Email is required');
  }
  
  const trimmed = email.trim().toLowerCase();
  
  // Basic email regex (more strict than browser default)
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailPattern.test(trimmed)) {
    return validationError('invalidEmail', 'Invalid email format');
  }
  
  // Length check
  if (trimmed.length > 254) {
    return validationError('emailTooLong', 'Email too long');
  }
  
  return { valid: true, sanitized: trimmed };
};

// Password strength validation
export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return validationError('passwordRequired', 'Password is required');
  }
  
  // Firebase minimum is 6, but we enforce stronger
  if (password.length < 8) {
    return validationError('passwordMinLength', 'Password must be at least 8 characters');
  }
  
  if (password.length > 128) {
    return validationError('passwordTooLong', 'Password too long');
  }
  
  // Check for at least one number and one letter
  if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(password)) {
    return validationError('passwordLetterNumber', 'Password must contain at least one letter and one number');
  }
  
  return { valid: true };
};

// Generic text sanitization for display
export const sanitizeForDisplay = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};
